import React, { useState, useEffect } from "react";
import { formatDate } from "../../requests/utilities/helpers";
import PrintShell, {
  SettingsGroup,
  OptionToggle,
  MultiSelect,
  SegmentedControl,
} from "./print/PrintShell";
import { COLOR_MODES } from "./print/WeeklyScheduleSheet";
import DailyScheduleSheet, { GROUP_MODES } from "./print/DailyScheduleSheet";

const long_date = { weekday: "long", month: "long", day: "numeric", year: "numeric" };
const short_day = { weekday: "short" };

export default function PrintModeDaily(props) {
  // Day selection is owned by PrintButton so mode switches can use it.
  const selectedDow = props.dowSelected ?? 0;
  const setSelectedDow = props.onDowChange || (() => {});
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [groupBy, setGroupBy] = useState("role");
  const [colorMode, setColorMode] = useState("role");
  const [showTimeline, setShowTimeline] = useState(true);
  const [showStations, setShowStations] = useState(true);
  const [showBreaks, setShowBreaks] = useState(false);
  const [showHours, setShowHours] = useState(!!props.printHoursShownEnabled);
  const [showOpenShifts, setShowOpenShifts] = useState(true);
  const [showNotes, setShowNotes] = useState(true);

  useEffect(() => {
    if (props.selectedRolesForFiltering) {
      setSelectedRoleIds(props.selectedRolesForFiltering);
    }
  }, [props.selectedRolesForFiltering]);

  const dow_nums = [0, 1, 2, 3, 4, 5, 6];
  const dayDate = props.addDays(props.week_start_date, selectedDow);
  const dateStr = formatDate(dayDate);
  const dayLabel = dayDate.toLocaleDateString("en-us", long_date);
  const storeName = props.storeNames?.find(
    (s) => s.id === Number(props.store_id)
  )?.store_name;

  const dayShifts = (props.actual_schedule || []).filter(
    (s) =>
      s.date === dateStr &&
      !s.other_store &&
      (!selectedRoleIds.length || selectedRoleIds.includes(s.role_id))
  );
  const shifts = dayShifts.filter((s) => s.status !== "AVAILABLE");
  const openShifts = dayShifts.filter((s) => s.status === "AVAILABLE");
  const dayNotes = (props.notes || []).filter((n) => n.date === dateStr);

  const roleOptions = (props.role_names || []).map((r) => ({
    value: r.id,
    label: r.role_name,
  }));
  const dayOptions = dow_nums.map((dow) => ({
    value: dow,
    label: props.addDays(props.week_start_date, dow).toLocaleDateString("en-us", short_day),
  }));

  const settings = (
    <>
      <SettingsGroup title="Day">
        <SegmentedControl value={selectedDow} onChange={setSelectedDow} options={dayOptions} />
      </SettingsGroup>

      <SettingsGroup title="Roles">
        <MultiSelect
          value={selectedRoleIds}
          onChange={setSelectedRoleIds}
          options={roleOptions}
          placeholder="All roles"
        />
      </SettingsGroup>

      <SettingsGroup title="Group by">
        <SegmentedControl value={groupBy} onChange={setGroupBy} options={GROUP_MODES} />
      </SettingsGroup>

      <SettingsGroup title="Color shifts by">
        <SegmentedControl value={colorMode} onChange={setColorMode} options={COLOR_MODES} />
      </SettingsGroup>

      <SettingsGroup title="Include">
        <OptionToggle
          label="Timeline bars"
          hint="Start-to-end bar for each shift"
          checked={showTimeline}
          onChange={setShowTimeline}
        />
        <OptionToggle label="Hours" checked={showHours} onChange={setShowHours} />
        <OptionToggle
          label="Open shifts"
          hint={openShifts.length ? `${openShifts.length} today` : "None today"}
          checked={showOpenShifts}
          onChange={setShowOpenShifts}
          disabled={!openShifts.length}
        />
        <OptionToggle label="Stations" checked={showStations} onChange={setShowStations} />
        <OptionToggle label="Break times" checked={showBreaks} onChange={setShowBreaks} />
        <OptionToggle
          label="Notes"
          hint={dayNotes.length ? `${dayNotes.length} today` : "None today"}
          checked={showNotes}
          onChange={setShowNotes}
          disabled={!dayNotes.length}
        />
      </SettingsGroup>
    </>
  );

  return (
    <PrintShell
      title="Print day"
      subtitle={dayLabel}
      documentTitle={`Schedule ${dayLabel}`}
      onClose={props.onClose}
      settings={settings}
      orientation="portrait"
      modeSwitcher={props.modeSwitcher}
      loading={props.scheduleLoading}
    >
      <DailyScheduleSheet
        title={storeName || "Daily schedule"}
        subtitle={dayLabel}
        shifts={shifts}
        openShifts={openShifts}
        roleNames={props.role_names}
        notes={dayNotes}
        options={{
          groupBy,
          colorMode,
          showTimeline,
          showStations,
          showBreaks,
          showHours,
          showOpenShifts,
          showNotes,
        }}
      />
    </PrintShell>
  );
}
